
import React, { useEffect, useState, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Button,
    Label,
    Spinner,
    Input,
    Pagination,
    PaginationItem,
    PaginationLink
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getReporteActas, getReporteActasExcel } from "../../helpers/backend";
import { toast, ToastContainer } from "react-toastify";
import "flatpickr/dist/themes/material_blue.css";
import "./ReporteAgentes.css"; // Reuse the premium styles

const ReporteActas = () => {
    const [reportData, setReportData] = useState(null);
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // Filters
    const [dateRange, setDateRange] = useState([new Date(new Date().getFullYear(), 0, 1), new Date()]);
    const [tipoOrden, setTipoOrden] = useState(null);
    const [aceptado, setAceptado] = useState({ label: "Todos", value: "" });
    const [cuentaContrato, setCuentaContrato] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(30);

    const tipoOrdenOptions = [
        { label: "Todas", value: "" },
        { value: "RCD", label: "RCD" },
        { value: "PQRS", label: "PQRS" },
        { value: "PROCESOS COMERCIALES", label: "PROCESOS COMERCIALES" },
        { value: "CORRESPONDENCIA", label: "CORRESPONDENCIA" }
    ];

    const aceptadoOptions = [
        { label: "Todos", value: "" },
        { label: "Aceptados", value: "true" },
        { label: "Pendientes", value: "false" }
    ];

    const fetchReport = useCallback((page = 1) => {
        setLoading(true);
        const params = {
            limit: limit,
            offset: (page - 1) * limit
        };

        if (dateRange && dateRange.length === 2) {
            params.fecha_inicio = dateRange[0]?.toISOString().split("T")[0];
            params.fecha_fin = dateRange[1]?.toISOString().split("T")[0];
        }

        if (tipoOrden && tipoOrden.value) params.tipo_orden = tipoOrden.value;
        if (aceptado && aceptado.value !== "") params.aceptado = aceptado.value;
        if (cuentaContrato) params.cuenta_contrato = cuentaContrato;

        getReporteActas(params)
            .then((res) => {
                const data = res.data || res;
                // Based on user sample: { count, results: { total_aceptados, total_pendientes, items } }
                setTotalCount(data.count || 0);
                setReportData(data.results || data);
                setItems(data.results?.items || data.items || []);
            })
            .catch((err) => {
                console.error("Error fetching report", err);
                toast.error("Error al cargar el reporte de actas.");
            })
            .finally(() => setLoading(false));
    }, [dateRange, tipoOrden, aceptado, cuentaContrato, limit]);

    useEffect(() => {
        fetchReport(currentPage);
    }, [currentPage, fetchReport]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchReport(1);
    };

    const handleResetFilters = () => {
        setDateRange([new Date(new Date().getFullYear(), 0, 1), new Date()]);
        setTipoOrden(null);
        setAceptado({ label: "Todos", value: "" });
        setCuentaContrato("");
        setCurrentPage(1);
        // fetchReport will be triggered by useEffect due to dateRange/currentPage change
    };

    const handleDownloadExcel = () => {
        const params = {};

        if (dateRange && dateRange.length === 2) {
            params.fecha_inicio = dateRange[0]?.toISOString().split("T")[0];
            params.fecha_fin = dateRange[1]?.toISOString().split("T")[0];
        }

        if (tipoOrden && tipoOrden.value) params.tipo_orden = tipoOrden.value;
        if (aceptado && aceptado.value !== "") params.aceptado = aceptado.value;
        if (cuentaContrato) params.cuenta_contrato = cuentaContrato;

        toast.info("Generando Excel...");
        getReporteActasExcel(params)
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Reporte_Actas_${params.fecha_inicio}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((err) => {
                console.error("Error downloading excel", err);
                toast.error("Error al descargar el Excel.");
            });
    };

    const colors = {
        primary: "#4F46E5",
        success: "#10B981",
        warning: "#F59E0B",
        bg_soft: {
            primary: "#EEF2FF",
            success: "#ECFDF5",
            warning: "#FFFBEB"
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleString();
    };

    return (
        <React.Fragment>
            <div className="ra-page-content page-content">
                <Container fluid>
                    <Breadcrumbs title="Reportes" breadcrumbItem="Reporte de Actas" />
                    <ToastContainer />

                    {/* Filters Section */}
                    <div className="ra-filter-container">
                        <Row className="align-items-end gy-3">
                            <Col lg={3} md={6}>
                                <Label className="ra-label">Rango de Fechas</Label>
                                <Flatpickr
                                    className="form-control"
                                    options={{ mode: "range", dateFormat: "Y-m-d", defaultDate: dateRange }}
                                    value={dateRange}
                                    onChange={(date) => setDateRange(date)}
                                />
                            </Col>
                            <Col lg={2} md={6}>
                                <Label className="ra-label">Tipo de Orden</Label>
                                <Select
                                    value={tipoOrden}
                                    onChange={(opt) => setTipoOrden(opt)}
                                    options={tipoOrdenOptions}
                                    placeholder="Todos"
                                    isClearable
                                />
                            </Col>
                            <Col lg={2} md={6}>
                                <Label className="ra-label">Estado Aceptación</Label>
                                <Select
                                    value={aceptado}
                                    onChange={(opt) => setAceptado(opt)}
                                    options={aceptadoOptions}
                                />
                            </Col>
                            <Col lg={2} md={6}>
                                <Label className="ra-label">Cuenta Contrato</Label>
                                <Input
                                    type="text"
                                    value={cuentaContrato}
                                    onChange={(e) => setCuentaContrato(e.target.value)}
                                    placeholder="12345678"
                                    style={{ borderRadius: '8px', padding: '0.6rem' }}
                                />
                            </Col>
                            <Col lg={3} md={12} className="d-flex gap-2">
                                <Button
                                    style={{ backgroundColor: colors.primary, borderColor: colors.primary, borderRadius: '8px', fontWeight: 600, flex: 1.5 }}
                                    onClick={handleSearch}
                                    disabled={loading}
                                >
                                    {loading ? <Spinner size="sm" /> : <><i className="bx bx-search-alt me-2"></i>Buscar</>}
                                </Button>
                                <Button
                                    outline
                                    style={{ color: colors.warning, borderColor: colors.warning, borderRadius: '8px', fontWeight: 600, flex: 1 }}
                                    onClick={handleResetFilters}
                                    disabled={loading}
                                >
                                    <i className="bx bx-refresh me-1"></i>Limpiar
                                </Button>
                                <Button
                                    style={{ backgroundColor: colors.success, borderColor: colors.success, borderRadius: '8px', fontWeight: 600, flex: 1.2 }}
                                    onClick={handleDownloadExcel}
                                    disabled={loading}
                                >
                                    <i className="bx bx-download me-1"></i>Excel
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    {/* Summary Section */}
                    {reportData && (
                        <Row className="mb-4">
                            <Col md={3}>
                                <div className="ra-card ra-stat-card">
                                    <div className="ra-stat-content flex-grow-1">
                                        <p>Total Actas</p>
                                        <h4>{reportData.total_aceptados + reportData.total_pendientes || 0}</h4>
                                    </div>
                                    <div className="ra-icon-box ra-icon-primary">
                                        <i className="bx bx-file"></i>
                                    </div>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="ra-card ra-stat-card">
                                    <div className="ra-stat-content flex-grow-1">
                                        <p>Aceptadas</p>
                                        <h4 className="text-success">{reportData.total_aceptados || 0}</h4>
                                    </div>
                                    <div className="ra-icon-box ra-icon-success">
                                        <i className="bx bx-check-circle"></i>
                                    </div>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="ra-card ra-stat-card">
                                    <div className="ra-stat-content flex-grow-1">
                                        <p>Pendientes</p>
                                        <h4 className="text-warning">{reportData.total_pendientes || 0}</h4>
                                    </div>
                                    <div className="ra-icon-box ra-icon-warning">
                                        <i className="bx bx-time"></i>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Table Section */}
                    <div className="ra-card">
                        <div className="ra-card-header">
                            <h5 className="ra-card-title"><i className="bx bx-list-ul text-primary"></i> Detalle de Actas Enviadas</h5>
                        </div>
                        <div className="ra-card-body p-0">
                            <div className="table-responsive">
                                <table className="ra-table">
                                    <thead>
                                        <tr>
                                            <th>OT / Caso</th>
                                            <th>Cliente</th>
                                            <th>Cuenta</th>
                                            <th>Teléfono</th>
                                            <th>Tipo</th>
                                            <th className="text-center">Aceptado</th>
                                            <th>Fecha Envío Política</th>
                                            <th>Estado WA</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.length > 0 ? (
                                            items.map((item, key) => (
                                                <tr key={key}>
                                                    <td>
                                                        <div className="fw-bold text-primary">{item.caso_nombre}</div>
                                                        <small className="text-muted">ID: {item.caso_id}</small>
                                                    </td>
                                                    <td>{item.cliente}</td>
                                                    <td>{item.cuenta_contrato}</td>
                                                    <td>{item.telefono}</td>
                                                    <td>
                                                        <span className="ra-badge ra-badge-primary">{item.tipo_orden}</span>
                                                        <br />
                                                        <small>{item.tipo_caso}</small>
                                                    </td>
                                                    <td className="text-center">
                                                        {item.aceptado ? (
                                                            <div className="d-flex flex-column align-items-center">
                                                                <span className="ra-badge ra-badge-success">SÍ</span>
                                                                <small className="font-size-11 mt-1">{formatDate(item.fecha_aceptacion)}</small>
                                                            </div>
                                                        ) : (
                                                            <span className="ra-badge ra-badge-warning">PENDIENTE</span>
                                                        )}
                                                    </td>
                                                    <td>{formatDate(item.fecha_envio_politica)}</td>
                                                    <td>
                                                        <span className={`ra-badge ${item.estado_whatsapp === "read" ? "ra-badge-success" : item.estado_whatsapp === "delivered" ? "ra-badge-primary" : "ra-badge-warning"}`}>
                                                            {item.estado_whatsapp?.toUpperCase() || "SENT"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center p-4">
                                                    {loading ? <Spinner color="primary" /> : "No se encontraron resultados para los filtros aplicados."}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalCount > limit && (
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination aria-label="Navegación de reporte">
                                <PaginationItem disabled={currentPage === 1}>
                                    <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                                </PaginationItem>
                                {[...Array(Math.ceil(totalCount / limit))].map((_, i) => (
                                    <PaginationItem active={i + 1 === currentPage} key={i}>
                                        <PaginationLink onClick={() => setCurrentPage(i + 1)}>
                                            {i + 1}
                                        </PaginationLink>
                                    </PaginationItem>
                                )).slice(Math.max(0, currentPage - 3), Math.min(Math.ceil(totalCount / limit), currentPage + 2))}
                                <PaginationItem disabled={currentPage === Math.ceil(totalCount / limit)}>
                                    <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                                </PaginationItem>
                            </Pagination>
                        </div>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ReporteActas;
